import { MutationTypes } from '@/modules/gem-meter/store/mutations';
import { $gfFrame } from '@/app/index';
import type { StandardState, GemMeterStandard, ScreenType } from '../types/types';
import CHECK_LIST, { PRIORITY } from '../constants/rulesConstants';
import { Deg } from '../utils';
import { useStore } from '@/store';
import { MODULE_NAME } from '../constants/moduleConstants';
import { watchEffect } from 'vue';
import useLoadingAction from './useLoadingAction';
/**
 * `useGemMeter` is a function that returns an object with properties that are used to display the
 * GemMeter
 * @returns An object with the following properties:
 */
let isFirst = true;
const useGemMeter = () => {
  const store = useStore();

  const { groupLoading, previousScreen } = useLoadingAction();

  const openModal = computed(() => store.state.gemMeter.openModal);
  const gemMeterOBD = computed(() => store.state.app.shopMeter.onboarding);
  const advanceForm = computed(() => store.getters['page/advanceForm']);
  const generalForm = computed(() => store.getters['page/generalForm']);
  const gemMeterStore = computed(() => store.state.gemMeter);
  const screenType = computed<ScreenType>(() => store.state.app.currentScreen);

  const resetGroupItem = () => {
    store.commit(`${MODULE_NAME}/${MutationTypes.RESET_GROUP_ITEM}`);
  };

  const cancelModal = async () => {
    store.commit(`${MODULE_NAME}/${MutationTypes.OPEN_MODAL_CHECKLIST}`, false);
  };

  const isInValidPage = (item: GemMeterStandard): boolean => {
    return !!(item.isPageValidate && item.screen[screenType.value].status === 'error');
  };

  const getErrorElements = (item: GemMeterStandard) => {
    if (!Object.keys(item.screen[screenType.value].errorElements).length) {
      return [];
    }

    return Object.values(item.screen[screenType.value].errorElements).filter((item) => item.visible);
  };

  // const itemLengthSort = (item: GemMeterStandard) => {
  //   if (!item) return 0;
  //   const errorElements = getErrorElements(item).filter((item) => item.status === 'new' && item.visible) || [];
  //   if (errorElements.length === 0) {
  //     return 0;
  //   }

  //   return errorElements.length;
  // };

  const getErrorElementsByGroup = (item: GemMeterStandard) => {
    if (!item) {
      return [];
    }

    const items = {
      ...(store.state.gemMeter?.shopableStandards || {}),
      ...(store.state.gemMeter?.convertableStandards || {}),
    } as StandardState;
    const errors = Object.values(items[item.id].screen[screenType.value].errorElements);
    return errors.filter((item) => item.visible);
  };

  /* Sorting the items in the checklist by the number of errors. */
  const group = computed(() => {
    const screenT = groupLoading.value ? previousScreen.value : screenType.value;
    let items = [
      ...Object.values(gemMeterStore.value.shopableStandards as StandardState),
      ...Object.values(gemMeterStore.value.convertableStandards as StandardState),
    ];

    items = items.filter((item: GemMeterStandard) => {
      if (item.id === CHECK_LIST.MISSING_LAZY_LOADING.id) {
        return (
          item.screen[screenT].status === 'error' ||
          (item.screen[screenT].status !== null && advanceForm.value.lazyLoad)
        );
      }

      if (item.id === CHECK_LIST.WEAK_SENSE_OF_TRUST.id) {
        const $productElement = $gfFrame().contents().find(`.module-wrap[data-key=product]`);
        return $productElement.length > 0;
      }

      return (
        item.screen[screenT].elementLength ||
        (item.isPageValidate && item.screen[screenT].status !== null) ||
        isInValidPage(item)
      );
    });

    return items.sort((a, b) => {
      return sort(a, b);
    });
  });

  const sort = (a: GemMeterStandard, b: GemMeterStandard) => {
    const aValid = a.isPageValidate ? (isInValidPage(a) ? 0 : 1) : getErrorElementsByGroup(a).length > 0 ? 0 : 1;
    const bValid = b.isPageValidate ? (isInValidPage(b) ? 0 : 1) : getErrorElementsByGroup(b).length > 0 ? 0 : 1;

    // Sort by group has error above group that already fixed
    if (aValid > bValid) {
      return 1;
    } else if (aValid < bValid) {
      return -1;
    }

    // Sort by priority of group
    //@ts-ignore
    if (convertPriorityToNumber(CHECK_LIST[a.key]?.priority) > convertPriorityToNumber(CHECK_LIST[b.key]?.priority)) {
      return -1;
    }
    //@ts-ignore
    else if (
      convertPriorityToNumber(CHECK_LIST[a.key]?.priority) < convertPriorityToNumber(CHECK_LIST[b.key]?.priority)
    ) {
      return 1;
    }

    // Sort by number of error in a group
    if (getErrorElementsByGroup(a).length > getErrorElementsByGroup(b).length) {
      return -1;
    } else if (getErrorElementsByGroup(a).length < getErrorElementsByGroup(b).length) {
      return 1;
    }

    return 0;
  };

  const convertPriorityToNumber = (priority: string) => {
    return priority === PRIORITY.CRITICAL ? 2 : priority === PRIORITY.HIGH ? 1 : 0;
  };

  const getFirstItemGroup = () => {
    if (!group.value || !group.value.length) return;
    return store.commit(`${MODULE_NAME}/${MutationTypes.SET_GROUP_ITEM}`, group.value[0]);
  };

  const groupItem = computed<GemMeterStandard>(() => {
    if (store.state.gemMeter.groupItem) {
      return store.state.gemMeter.groupItem;
    }

    return null;
  });

  const hasErrorInGroup = computed(() => {
    const items = group.value;

    const firstItemError = items.find(
      (item) => Object.keys(item.screen[screenType.value].errorElements).length > 0 || isInValidPage(item),
    );

    return firstItemError;
  });

  watchEffect(() => {
    if (isFirst && group.value.length > 0 && hasErrorInGroup.value && openModal.value) {
      isFirst = false;
      getFirstItemGroup();
    }
  });

  const getTitle = (item: GemMeterStandard, hasFixed: boolean | undefined) => {
    if (!item) '';

    return hasFixed
      ? CHECK_LIST[item.key as keyof typeof CHECK_LIST].titleSuccess
      : CHECK_LIST[item.key as keyof typeof CHECK_LIST].title;
  };

  const getPriority = (item: GemMeterStandard) => {
    if (!item) '';
    return CHECK_LIST[item.key as keyof typeof CHECK_LIST].priority;
  };

  const getDescription = (item: GemMeterStandard, hasFixed: boolean | undefined) => {
    if (!item) '';

    return hasFixed
      ? CHECK_LIST[item.key as keyof typeof CHECK_LIST].descriptionSuccess
      : CHECK_LIST[item.key as keyof typeof CHECK_LIST].description;
  };

  const getPropertyName = (item: GemMeterStandard) => {
    if (!item) return;
    return CHECK_LIST[item.key as keyof typeof CHECK_LIST].propertyName;
  };

  const getActivePositionByPropertyElement = (item: GemMeterStandard) => {
    if (!item) return;
    return CHECK_LIST[item.key as keyof typeof CHECK_LIST].activePositionByPropertyElement;
  };

  const getCounter = computed(() => {
    if (!groupItem.value) {
      return 0;
    }

    return Object.values(groupItem.value.screen[screenType.value].errorElements).filter((item) => item.status === 'new')
      .length;
  });

  const degProgressValue = computed(() => {
    return Deg(gemMeterStore.value.metric[screenType.value]);
  });

  return {
    openModal,
    cancelModal,
    gemMeterOBD,
    screenType,
    group,
    getTitle,
    getPriority,
    getDescription,
    groupItem,
    getErrorElements,
    isInValidPage,
    hasErrorInGroup,
    getCounter,
    getActivePositionByPropertyElement,
    getPropertyName,
    getErrorElementsByGroup,
    degProgressValue,
    resetGroupItem,
    currentIconScreen: computed(() => {
      const icons: Record<string, string> = {
        lg: 'icon-desktop',
        md: 'icon-laptop',
        sm: 'icon-tablet',
        xs: 'icon-mobile',
      };

      return icons[store.state.app.currentScreen];
    }),
    getFirstItemGroup,
    advanceForm,
    generalForm,
  };
};

export { useGemMeter as default };
